import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Plus, Building, Loader2, Search, Pencil, Trash2, MapPin, DollarSign, ChevronRight
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "./ui/dialog";
import { propertyService } from '../services/propertyService';

const Properties = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    zipCode: '',
    units: '',
    monthlyRent: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const data = await propertyService.getAll();
      setProperties(data);
    } catch (error) {
      console.error('Failed to fetch properties:', error);
      setError('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const openAdd = () => {
    setFormData({ name: '', address: '', zipCode: '', units: '', monthlyRent: '' });
    setError('');
    setIsAddOpen(true);
  };

  const openEdit = (property) => {
    setSelectedProperty(property);
    setFormData({
      name: property.name,
      address: property.address,
      zipCode: property.zipCode || '',
      units: property.units?.toString() || '1',
      monthlyRent: property.monthlyRent?.toString() || '0'
    });
    setError('');
    setIsEditOpen(true);
  };

  const openDelete = (property) => {
    setSelectedProperty(property);
    setIsDeleteOpen(true);
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await propertyService.create({
        ...formData,
        units: parseInt(formData.units, 10) || 1,
        monthlyRent: parseFloat(formData.monthlyRent) || 0
      });
      await fetchProperties();
      setIsAddOpen(false);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to create property');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await propertyService.update(selectedProperty._id, {
        ...formData,
        units: parseInt(formData.units, 10) || 1,
        monthlyRent: parseFloat(formData.monthlyRent) || 0
      });
      await fetchProperties();
      setIsEditOpen(false);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update property');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    setSubmitting(true);
    try {
      await propertyService.delete(selectedProperty._id);
      await fetchProperties();
      setIsDeleteOpen(false);
    } catch (error) {
      console.error('Failed to delete property:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredProperties = properties.filter((p) => {
    const q = searchQuery.toLowerCase();
    return (
      p.name.toLowerCase().includes(q) ||
      p.address.toLowerCase().includes(q)
    );
  });

  const renderPropertyForm = (onSubmit, submitLabel) => (
    <form onSubmit={onSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 text-red-800 px-4 py-3 rounded text-sm">{error}</div>
      )}
      <div className="space-y-2">
        <Label htmlFor="name">Property Name</Label>
        <Input
          id="name"
          name="name"
          placeholder="e.g. Sunset Apartments"
          value={formData.name}
          onChange={handleInputChange}
          required
          disabled={submitting}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="address">Address</Label>
        <Input
          id="address"
          name="address"
          placeholder="123 Main St, City, State"
          value={formData.address}
          onChange={handleInputChange}
          required
          disabled={submitting}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="zipCode">ZIP Code</Label>
        <Input
          id="zipCode"
          name="zipCode"
          placeholder="90210"
          value={formData.zipCode}
          onChange={handleInputChange}
          disabled={submitting}
        />
        <p className="text-xs text-muted-foreground">Used to find nearby vendors shared by other landlords.</p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="units">Number of Units</Label>
        <Input
          id="units"
          name="units"
          type="number"
          min="1"
          placeholder="1"
          value={formData.units}
          onChange={handleInputChange}
          required
          disabled={submitting}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="monthlyRent">Monthly Rent ($)</Label>
        <Input
          id="monthlyRent"
          name="monthlyRent"
          type="number"
          min="0"
          step="0.01"
          placeholder="0.00"
          value={formData.monthlyRent}
          onChange={handleInputChange}
          disabled={submitting}
        />
      </div>
      <DialogFooter>
        <Button type="submit" disabled={submitting}>
          {submitting ? `${submitLabel}...` : submitLabel}
        </Button>
      </DialogFooter>
    </form>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold tracking-tight">Properties</h2>
        <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
          <DialogTrigger asChild>
            <Button onClick={openAdd}>
              <Plus className="mr-2 h-4 w-4" /> Add Property
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Property</DialogTitle>
            </DialogHeader>
            {renderPropertyForm(handleSubmitAdd, "Add Property")}
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center space-x-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search properties..."
            className="pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>
      
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredProperties.map((property) => (
          <Card
            key={property._id}
            className="hover:shadow-md transition-shadow cursor-pointer group"
            onClick={() => navigate(`/properties/${property._id}`)}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-lg font-medium group-hover:text-primary transition-colors">
                {property.name}
              </CardTitle>
              <Building className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>{property.address}</div>
              </div>
              <div className="flex justify-between text-sm font-medium">
                <span>Units: {property.units}</span>
                <span className="flex items-center gap-1">
                  <DollarSign className="h-3 w-3" />
                  {(property.monthlyRent || 0).toLocaleString()}/mo
                </span>
              </div>
              <div className="flex gap-1 justify-end pt-1" onClick={(e) => e.stopPropagation()}>
                <Dialog open={isEditOpen && selectedProperty?._id === property._id} onOpenChange={(open) => open ? openEdit(property) : setIsEditOpen(false)}>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(property)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Edit Property</DialogTitle>
                    </DialogHeader>
                    {renderPropertyForm(handleSubmitEdit, "Update Property")}
                  </DialogContent>
                </Dialog>

                <Dialog open={isDeleteOpen && selectedProperty?._id === property._id} onOpenChange={(open) => open ? openDelete(property) : setIsDeleteOpen(false)}>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="sm" onClick={() => openDelete(property)}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Delete Property</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">
                      Are you sure you want to delete <strong>{selectedProperty?.name}</strong>? This action cannot be undone.
                    </p>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setIsDeleteOpen(false)} disabled={submitting}>
                        Cancel
                      </Button>
                      <Button type="button" variant="destructive" onClick={handleConfirmDelete} disabled={submitting}>
                        {submitting ? 'Deleting...' : 'Delete'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <Button variant="ghost" size="sm" className="text-muted-foreground">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredProperties.length === 0 && (
          <Card className="col-span-full border-dashed">
            <CardContent className="flex flex-col items-center justify-center h-40 text-muted-foreground">
              <Building className="h-8 w-8 mb-2 opacity-20" />
              <p>No properties found. Add your first property to get started.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Properties;
